import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTask,
  updateTask,
  listTaskLinks,
  createTaskLink,
  deleteTaskLink,
  listTaskAttachments,
  uploadTaskAttachment,
  downloadTaskAttachmentUrl,
  deleteTaskAttachment,
  listTaskComments,
  createTaskComment,
  updateTaskComment,
  deleteTaskComment,
  listEvents,
  listEmployees,
  listAssignments,
  archiveTask,
  unarchiveTask,
  type Task,
  type TaskLink,
  type TaskAttachment,
  type TaskComment,
  type Event,
  type Employee,
  type Assignment,
} from '../../lib/api'
import { AssigneeControl } from '../assignment/AssigneeControl'
import { ClientContextPanel } from '../client/ClientKnowledge'
import { StatusSelect, DueDateInput, DisciplineSelect, ClientLabel } from './TaskControls'
import { useDisciplines } from '../../hooks/useDisciplines'
import { useTaskAttachmentUpload, pastedFileName } from '../../hooks/useTaskAttachments'
import { useUpdateTask } from '../../hooks/useTaskMutations'
import { useSession } from '../../hooks/useAuth'
import { roleCanSeeAll, roleCanWrite, roleCanBrowseClients } from '@shared/enums'
import {
  X,
  Save,
  Link2,
  Paperclip,
  MessageSquare,
  Activity,
  Plus,
  Trash2,
  ExternalLink,
  Link as LinkIcon,
  Check,
  Edit3,
  FileText,
  Image,
  File,
  Clock,
  Send,
  Building2,
  Archive,
  ArchiveRestore,
} from 'lucide-react'
import { DownloadButton } from '../DownloadButton'
import { taskUrl } from '@shared/links'

export type TaskDetailTab = 'details' | 'client' | 'links' | 'files' | 'comments' | 'activity'
type Tab = TaskDetailTab

interface TaskDetailProps {
  taskId: string
  onClose: () => void
  /** Tab to open on — e.g. Files, after a new task's upload failed. */
  initialTab?: TaskDetailTab
  /** A one-off message above the tabs, e.g. what didn't save at creation. */
  notice?: string | null
}

export function TaskDetail({ taskId, onClose, initialTab = 'details', notice = null }: TaskDetailProps) {
  const qc = useQueryClient()
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  // `canWrite` is "may work the task" — every role but read_only, matching the
  // API's `requireWrite`. On the Team role (LXP-55) that covers editing,
  // status, files, comments, archiving, and handing the task to anyone on the
  // team. Permanent delete is the one admin-only action, and the card has no
  // delete control. `canOpenClients` links through to the client page.
  const canWrite = roleCanWrite(role)
  const canOpenClients = roleCanBrowseClients(role)
  // An assignment-scoped role loses the task from its list when it drops its
  // last seat, so the assignee control confirms that and keeps a hand-off giver on.
  const scopedToAssigned = !roleCanSeeAll(role)
  const currentEmployeeId = (session?.user as { employeeId?: string } | undefined)?.employeeId
  const disciplines = useDisciplines()
  const [tab, setTab] = useState<Tab>(initialTab)
  const [showNotice, setShowNotice] = useState(!!notice)

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Core task data
  const { data: taskData, isLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => getTask(taskId),
    enabled: !!taskId,
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => listEmployees(),
  })

  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments', { entityType: 'task', entityId: taskId }],
    queryFn: () => listAssignments({ entityType: 'task', entityId: taskId }),
    enabled: !!taskId,
  })

  const employees = employeesData?.items ?? []
  const assignments = assignmentsData?.items ?? []

  // Screenshots are pasted, not browsed for. The handler sits on the whole
  // panel rather than the Files tab so Cmd-V works while you're typing up the
  // notes — which is when you actually have the screenshot on the clipboard.
  const { upload: uploadPasted } = useTaskAttachmentUpload(taskId)
  const handlePaste = (e: React.ClipboardEvent) => {
    if (!canWrite) return
    const files = Array.from(e.clipboardData?.files ?? [])
    if (!files.length) return
    // Only intercept when the clipboard carries a file. Pasting text into the
    // notes box or a comment must stay untouched.
    e.preventDefault()
    const stamp = new Date().toISOString()
    void uploadPasted(files, (f) => pastedFileName(f, stamp)).then(() => setTab('files'))
  }

  const archiveMutation = useMutation({
    mutationFn: (archived: boolean) => (archived ? unarchiveTask(taskId) : archiveTask(taskId)),
    onSuccess: (_data, wasArchived) => {
      qc.invalidateQueries({ queryKey: ['task', taskId] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      if (!wasArchived) onClose() // archived → task leaves the current view
    },
  })

  const TABS: { key: Tab; label: string; icon: typeof Activity }[] = [
    { key: 'details', label: 'Details', icon: FileText },
    ...(taskData?.clientId ? [{ key: 'client' as Tab, label: 'Client', icon: Building2 }] : []),
    { key: 'links', label: 'Links', icon: Link2 },
    { key: 'files', label: 'Files', icon: Paperclip },
    { key: 'comments', label: 'Comments', icon: MessageSquare },
    { key: 'activity', label: 'Activity', icon: Activity },
  ]

  if (isLoading || !taskData) {
    return (
      <div className="task-detail-backdrop" onClick={onClose}>
        <div className="task-detail-panel" onClick={(e) => e.stopPropagation()}>
          <div className="task-detail-panel__loading">
            <div className="loading-spinner" />
          </div>
        </div>
      </div>
    )
  }

  const task = taskData

  return (
    <div className="task-detail-backdrop" onClick={onClose}>
      <div className="task-detail-panel" onClick={(e) => e.stopPropagation()} onPaste={handlePaste}>
        {/* Header */}
        <div className="task-detail-panel__header">
          <div className="task-detail-panel__title-row">
            <span className={`priority-dot priority-${task.priority}`} />
            <TaskTitle task={task} canWrite={canWrite} />
          </div>
          <div className="task-detail-panel__header-actions">
            <CopyLinkButton taskId={task.id} />
            {canWrite && (
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => archiveMutation.mutate(!!task.archivedAt)}
                disabled={archiveMutation.isPending}
                title={task.archivedAt ? 'Restore from archive' : 'Archive for future reference'}
              >
                {task.archivedAt ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                <span>{task.archivedAt ? 'Unarchive' : 'Archive'}</span>
              </button>
            )}
            <button className="btn-icon" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Client — the account this work belongs to, one click away. */}
        {task.clientName && (
          <div className="task-detail-panel__client">
            <ClientLabel
              clientId={task.clientId}
              clientName={task.clientName}
              linkTo={canOpenClients}
            />
          </div>
        )}

        {/* Meta row — status and due date are live controls, not labels, so a
            task can be re-statused or re-dated without leaving the card. */}
        <div className="task-detail-panel__meta">
          <StatusSelect task={task} disabled={!canWrite} />
          <DueDateInput task={task} disabled={!canWrite} />
          {task.archivedAt && (
            <span className="chip chip-sm task-detail-panel__archived-chip">
              <Archive size={11} /> Archived
            </span>
          )}
          <DisciplineSelect task={task} disciplines={disciplines} disabled={!canWrite} />
          {task.estimateHrs && (
            <span className="text-muted text-xs">{task.estimateHrs}h est.</span>
          )}
        </div>

        {/* Assignees */}
        <div className="task-detail-panel__assignees">
          <AssigneeControl
            entityType="task"
            entityId={taskId}
            assignments={assignments}
            employees={employees}
            canManage={canWrite}
            invalidateKeys={['task', 'tasks']}
            currentEmployeeId={currentEmployeeId}
            scopedToAssigned={scopedToAssigned}
          />
        </div>

        {notice && showNotice && (
          <div className="task-detail-panel__notice" role="alert">
            <span>{notice}</span>
            <button className="btn-icon" onClick={() => setShowNotice(false)} aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="task-detail-panel__tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`task-detail-panel__tab${tab === key ? ' task-detail-panel__tab--active' : ''}`}
              onClick={() => setTab(key)}
            >
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="task-detail-panel__body">
          {tab === 'details' && (
            <DetailsTab task={task} canWrite={canWrite} />
          )}
          {tab === 'client' && task.clientId && (
            <ClientContextPanel clientId={task.clientId} />
          )}
          {tab === 'links' && (
            <LinksTab taskId={taskId} canWrite={canWrite} />
          )}
          {tab === 'files' && (
            <FilesTab taskId={taskId} canWrite={canWrite} />
          )}
          {tab === 'comments' && (
            <CommentsTab taskId={taskId} employees={employees} currentEmployeeId={currentEmployeeId} />
          )}
          {tab === 'activity' && (
            <ActivityTab taskId={taskId} employees={employees} />
          )}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * TITLE — click to rename in place
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Copies a direct link to this task — the same `/tasks?task=<id>` deep link the
 * notification emails use (both come from `taskUrl` in src/shared/links.ts), so
 * a link pasted into Slack behaves exactly like one clicked out of an email.
 *
 * The recipient still needs a login and access to the task: this shares a
 * location, not the contents.
 */
function CopyLinkButton({ taskId }: { taskId: string }) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)

  // The clipboard API needs a secure context and can be refused outright, so a
  // rejection has to say so rather than silently look like it worked.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(taskUrl(window.location.origin, taskId))
      setFailed(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setFailed(true)
      setTimeout(() => setFailed(false), 2500)
    }
  }

  return (
    <button
      className="btn btn-ghost btn-xs"
      type="button"
      onClick={copy}
      title={failed ? 'Could not copy — copy the page URL instead' : 'Copy a direct link to this task'}
    >
      {copied ? <Check size={14} /> : <LinkIcon size={14} />}
      <span>{copied ? 'Copied' : failed ? 'Copy failed' : 'Copy link'}</span>
    </button>
  )
}

/**
 * Renaming matters more than it looks: a finished task that gets a new name and
 * a new date is how a recurring piece of work gets recycled ("…— review later")
 * rather than retyped from scratch.
 */
function TaskTitle({ task, canWrite }: { task: Task; canWrite: boolean }) {
  const update = useUpdateTask()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(task.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const commit = () => {
    const next = draft.trim()
    setEditing(false)
    // An empty title is a slip, not an intent — restore rather than save it.
    if (!next) return setDraft(task.title)
    if (next !== task.title) update.mutate({ id: task.id, patch: { title: next } })
  }

  if (!canWrite) return <h2 className="task-detail-panel__title">{task.title}</h2>

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="task-detail-panel__title-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') {
            setDraft(task.title)
            setEditing(false)
            // Swallow it — Escape here means "cancel the rename", not
            // "close the card the rename is in".
            e.stopPropagation()
          }
        }}
        aria-label="Task title"
      />
    )
  }

  return (
    <h2
      className="task-detail-panel__title task-detail-panel__title--editable"
      onClick={() => {
        setDraft(task.title)
        setEditing(true)
      }}
      title="Click to rename"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') { setDraft(task.title); setEditing(true) } }}
    >
      {task.title}
    </h2>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * DETAILS TAB — editable description / notes
 * ═══════════════════════════════════════════════════════════════════ */

function DetailsTab({ task, canWrite }: { task: Task & { assignments?: any[] }; canWrite: boolean }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [description, setDescription] = useState(task.description ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const saveMutation = useMutation({
    mutationFn: (desc: string) => updateTask(task.id, { description: desc || null } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task', task.id] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      setEditing(false)
    },
  })

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length,
      )
    }
  }, [editing])

  return (
    <div className="task-detail-section">
      <div className="task-detail-section__header">
        <h3>Notes & Description</h3>
        {!editing && canWrite && (
          <button className="btn btn-ghost btn-xs" onClick={() => setEditing(true)}>
            <Edit3 size={12} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="task-detail-section__edit">
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={8}
            placeholder="Add notes, context, links, or any detail about this task…"
            className="task-detail-textarea"
          />
          <div className="task-detail-section__actions">
            <button
              className="btn btn-primary btn-xs"
              onClick={() => saveMutation.mutate(description)}
              disabled={saveMutation.isPending}
            >
              <Save size={12} /> {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => {
                if (
                  description !== (task.description ?? '') &&
                  !confirm('Discard unsaved changes?')
                ) {
                  return
                }
                setDescription(task.description ?? '')
                setEditing(false)
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="task-detail-section__content">
          {task.description ? (
            <p className="task-detail-description">{task.description}</p>
          ) : (
            <p className="task-detail-empty">
              No notes yet. {canWrite ? 'Click Edit to add context, links, or instructions.' : ''}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * LINKS TAB
 * ═══════════════════════════════════════════════════════════════════ */

function LinksTab({ taskId, canWrite }: { taskId: string; canWrite: boolean }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['task-links', taskId],
    queryFn: () => listTaskLinks(taskId),
  })

  const addMutation = useMutation({
    mutationFn: () => createTaskLink(taskId, { url, label: label || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-links', taskId] })
      setUrl('')
      setLabel('')
      setAdding(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteTaskLink(taskId, linkId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-links', taskId] }),
  })

  const links = data?.items ?? []

  return (
    <div className="task-detail-section">
      <div className="task-detail-section__header">
        <h3>Links</h3>
        {canWrite && !adding && (
          <button className="btn btn-ghost btn-xs" onClick={() => setAdding(true)}>
            <Plus size={12} /> Add link
          </button>
        )}
      </div>

      {adding && (
        <div className="task-detail-link-form">
          <input
            type="url"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="task-detail-input"
            autoFocus
          />
          <input
            type="text"
            placeholder="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="task-detail-input"
          />
          <div className="task-detail-section__actions">
            <button
              className="btn btn-primary btn-xs"
              onClick={() => addMutation.mutate()}
              disabled={!url || addMutation.isPending}
            >
              Add
            </button>
            <button className="btn btn-ghost btn-xs" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted text-xs">Loading…</p>
      ) : links.length === 0 && !adding ? (
        <p className="task-detail-empty">No links attached yet.</p>
      ) : (
        <ul className="task-detail-link-list">
          {links.map((link) => (
            <li key={link.id} className="task-detail-link-item">
              <ExternalLink size={14} className="text-muted" />
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="task-detail-link-url"
                title={link.url}
              >
                {link.label || link.url}
              </a>
              {canWrite && (
                <button
                  className="task-detail-link-delete"
                  onClick={() => {
                    if (confirm(`Remove link "${link.label || link.url}"?`)) {
                      deleteMutation.mutate(link.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  aria-label="Remove link"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * FILES TAB
 * ═══════════════════════════════════════════════════════════════════ */

function FilesTab({ taskId, canWrite }: { taskId: string; canWrite: boolean }) {
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dropping, setDropping] = useState(false)
  const { upload, uploading, error: uploadError } = useTaskAttachmentUpload(taskId)

  const { data, isLoading } = useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: () => listTaskAttachments(taskId),
  })

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteTaskAttachment(taskId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-attachments', taskId] }),
  })

  const attachments = data?.items ?? []

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const duplicate = attachments.find((a) => a.filename === file.name)
      if (
        !duplicate ||
        confirm(`"${file.name}" is already attached. Upload another copy anyway?`)
      ) {
        void upload([file])
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div
      className={`task-detail-section${dropping ? ' task-detail-section--dropping' : ''}`}
      // Dropping a file onto the list is the other half of pasting one: both
      // beat "click Upload, then find the file in a dialog".
      onDragOver={canWrite ? (e) => { e.preventDefault(); setDropping(true) } : undefined}
      onDragLeave={canWrite ? () => setDropping(false) : undefined}
      onDrop={
        canWrite
          ? (e) => {
              e.preventDefault()
              setDropping(false)
              void upload(Array.from(e.dataTransfer.files ?? []))
            }
          : undefined
      }
    >
      <div className="task-detail-section__header">
        <h3>Files</h3>
        {canWrite && (
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Plus size={12} /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          onChange={handleFileChange}
          aria-label="Upload file"
        />
      </div>

      {uploadError && <p className="task-detail-upload-error">{uploadError}</p>}

      {isLoading ? (
        <p className="text-muted text-xs">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="task-detail-empty">
          No files attached yet.
          {canWrite ? ' Paste a screenshot, drop a file here, or use Upload.' : ''}
        </p>
      ) : (
        <ul className="task-detail-file-list">
          {attachments.map((att) => (
            <li key={att.id} className="task-detail-file-item">
              <FileIcon mimeType={att.mimeType} />
              <div className="task-detail-file-info">
                <span className="task-detail-file-name">{att.filename}</span>
                <span className="task-detail-file-meta text-muted text-xs">
                  {formatFileSize(att.sizeBytes)}
                  {' · '}
                  {new Date(att.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div className="task-detail-file-actions">
                <DownloadButton
                  url={downloadTaskAttachmentUrl(taskId, att.id)}
                  filename={att.filename}
                />
                {canWrite && (
                  <button
                    className="btn-icon"
                    onClick={() => {
                      if (confirm(`Delete "${att.filename}"? The file will be removed from storage.`)) {
                        deleteMutation.mutate(att.id)
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    aria-label="Delete file"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FileIcon({ mimeType }: { mimeType: string | null }) {
  if (mimeType?.startsWith('image/')) return <Image size={16} className="text-accent" />
  if (mimeType?.includes('pdf')) return <FileText size={16} className="text-danger" />
  return <File size={16} className="text-muted" />
}

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/* ═══════════════════════════════════════════════════════════════════
 * COMMENTS TAB
 * ═══════════════════════════════════════════════════════════════════ */

function CommentsTab({
  taskId,
  employees,
  currentEmployeeId,
}: {
  taskId: string
  employees: Employee[]
  currentEmployeeId?: string
}) {
  const qc = useQueryClient()
  const [body, setBody] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['task-comments', taskId],
    queryFn: () => listTaskComments(taskId),
  })

  const addMutation = useMutation({
    mutationFn: () => createTaskComment(taskId, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-comments', taskId] })
      setBody('')
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (commentId: string) => updateTaskComment(taskId, commentId, { body: editBody }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-comments', taskId] })
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => deleteTaskComment(taskId, commentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-comments', taskId] }),
  })

  const comments = data?.items ?? []
  const empMap = new Map(employees.map((e) => [e.id, e]))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return
    addMutation.mutate()
  }

  return (
    <div className="task-detail-section task-detail-comments">
      <div className="task-detail-comments__list">
        {isLoading ? (
          <p className="text-muted text-xs">Loading…</p>
        ) : comments.length === 0 ? (
          <p className="task-detail-empty">
            No comments yet. Start a thread to capture notes and context.
          </p>
        ) : (
          comments.map((comment) => {
            const author = empMap.get(comment.authorId ?? '')
            const isOwn = comment.authorId === currentEmployeeId
            const isEditing = editingId === comment.id

            return (
              <div key={comment.id} className="task-detail-comment">
                <div className="task-detail-comment__avatar">
                  <span
                    className="assignee-chip__avatar"
                    style={{
                      background: author?.avatarColor ? `${author.avatarColor}22` : undefined,
                      color: author?.avatarColor,
                    }}
                  >
                    {author?.initials ?? '?'}
                  </span>
                </div>
                <div className="task-detail-comment__content">
                  <div className="task-detail-comment__header">
                    <span className="task-detail-comment__author">
                      {author?.name ?? 'Unknown'}
                    </span>
                    <span className="task-detail-comment__time text-muted text-xs">
                      {formatTime(comment.createdAt)}
                    </span>
                    {isOwn && !isEditing && (
                      <div className="task-detail-comment__actions">
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setEditingId(comment.id)
                            setEditBody(comment.body)
                          }}
                          aria-label="Edit comment"
                        >
                          <Edit3 size={11} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => {
                            if (confirm('Delete this comment?')) deleteMutation.mutate(comment.id)
                          }}
                          aria-label="Delete comment"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="task-detail-comment__edit">
                      <textarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        rows={3}
                        className="task-detail-textarea task-detail-textarea--sm"
                        autoFocus
                      />
                      <div className="task-detail-section__actions">
                        <button
                          className="btn btn-primary btn-xs"
                          onClick={() => updateMutation.mutate(comment.id)}
                          disabled={!editBody.trim() || updateMutation.isPending}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="task-detail-comment__body">{comment.body}</p>
                  )}
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Comment input */}
      <form className="task-detail-comments__input" onSubmit={handleSubmit}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment…"
          rows={2}
          className="task-detail-textarea task-detail-textarea--sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              if (body.trim()) addMutation.mutate()
            }
          }}
        />
        <button
          type="submit"
          className="btn btn-primary btn-xs"
          disabled={!body.trim() || addMutation.isPending}
        >
          <Send size={12} /> {addMutation.isPending ? 'Posting…' : 'Post'}
        </button>
      </form>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * ACTIVITY TAB — events timeline
 * ═══════════════════════════════════════════════════════════════════ */

function ActivityTab({ taskId, employees }: { taskId: string; employees: Employee[] }) {
  const { data, isLoading } = useQuery({
    queryKey: ['events', { entityType: 'task', entityId: taskId }],
    queryFn: () => listEvents({ entityType: 'task', entityId: taskId, limit: 50 }),
  })

  const events = data?.items ?? []
  const empMap = new Map(employees.map((e) => [e.id, e]))

  if (isLoading) return <p className="text-muted text-xs">Loading…</p>
  if (events.length === 0) return <p className="task-detail-empty">No activity recorded yet.</p>

  const sorted = [...events].sort((a, b) => (b.at > a.at ? 1 : -1))

  return (
    <div className="task-detail-section">
      <div className="task-detail-timeline">
        {sorted.map((ev, i) => {
          const emp = empMap.get(ev.actor)
          const isLast = i === sorted.length - 1
          return (
            <div key={ev.id} className="task-detail-timeline__item">
              <div className="task-detail-timeline__rail">
                <span className="task-detail-timeline__dot">
                  {emp?.initials ?? 'SY'}
                </span>
                {!isLast && <span className="task-detail-timeline__line" />}
              </div>
              <div className="task-detail-timeline__content">
                <div className="task-detail-timeline__text">
                  <strong>{emp?.name ?? ev.actor}</strong>{' '}
                  {formatEventText(ev)}
                </div>
                <span className="task-detail-timeline__time mono text-muted text-xs">
                  {formatTime(ev.at)}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * HELPERS
 * ═══════════════════════════════════════════════════════════════════ */

function formatEventText(ev: Event): string {
  switch (ev.type) {
    case 'created':
      return 'created this task'
    case 'status_changed':
      return `moved from "${ev.fromState}" to "${ev.toState}"`
    case 'assigned':
      return `assigned ${(ev.meta as any)?.role ?? 'team member'}`
    case 'unassigned':
      return 'removed assignment'
    case 'field_changed':
      return `updated ${((ev.meta as any)?.fields as string[])?.join(', ') ?? 'fields'}`
    default:
      return ev.type.replace(/_/g, ' ')
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
